import React, { useState, useRef, useEffect } from 'react';

const ERAS = [
  {
    t: 0, // now
    label: 'Present day',
    hebrewLabel: 'עַכְשָׁיו — Now',
    temp: '~3K',
    energy: '< 1 eV',
    description: 'The Higgs field is active. All particles have their masses. The four forces are distinct. Atoms, molecules, stars, and life exist.',
    ruach: 'The Ruach Elohim sustains all form. Every particle holds the weight given to it. Creation continues.',
    scripture: '"He is before all things, and in him all things hold together." — Colossians 1:17',
    particles: { electron: true, quark: true, w: true, z: true, photon: true, higgs: true },
    forcesMerged: [],
  },
  {
    t: 0.15,
    label: 'Stellar era',
    hebrewLabel: 'כּוֹכָבִים — Stars',
    temp: '~10⁶K',
    energy: '~100 eV',
    description: 'Stars burn by nuclear fusion. Protons fuse into helium, releasing enormous energy. The elements are forged inside stellar cores.',
    ruach: '"He determines the number of the stars and calls them each by name." — Psalm 147:4. The Ruach breath held in each proton powers the stars.',
    scripture: 'Psalm 147:4',
    particles: { electron: true, quark: true, w: true, z: true, photon: true, higgs: true },
    forcesMerged: [],
  },
  {
    t: 0.3,
    label: 'Big Bang nucleosynthesis',
    hebrewLabel: 'רֵאשִׁית — Beginning',
    temp: '~10⁹K',
    energy: '~100 keV',
    description: 'Three minutes after the Big Bang. Protons and neutrons fuse into the first nuclei — hydrogen, helium, lithium. The light elements are born.',
    ruach: 'The first binding. Quarks have already received the Ruach breath. Now they bind together, their confinement energy forming the first nuclei.',
    scripture: '"In the beginning God created the heavens and the earth." — Genesis 1:1',
    particles: { electron: true, quark: true, w: true, z: true, photon: true, higgs: true },
    forcesMerged: [],
  },
  {
    t: 0.5,
    label: 'Quark-hadron transition',
    hebrewLabel: 'קְשִׁירָה — Binding',
    temp: '~10¹²K',
    energy: '~150 MeV',
    description: 'One microsecond after the Big Bang. Free quarks bind into protons and neutrons for the first time. The universe transitions from quark-gluon plasma to hadronic matter.',
    ruach: 'The hidden bond takes hold. Free quarks — utterances floating unbound — are drawn together by the gluon\'s confinement. The first formed matter appears. "The earth was formless..." — this is the moment form begins.',
    scripture: 'Genesis 1:2 — form beginning to emerge from the void',
    particles: { electron: true, quark: true, w: false, z: false, photon: true, higgs: true },
    forcesMerged: [],
    note: 'W & Z bosons too short-lived at this energy to persist',
  },
  {
    t: 0.68,
    label: 'Electroweak era',
    hebrewLabel: 'אַחְדוּת — Unity',
    temp: '~10¹⁵K',
    energy: '~100 GeV',
    description: 'One picosecond after the Big Bang. Above the Higgs symmetry breaking scale. The electromagnetic and weak forces are now unified into a single electroweak force.',
    ruach: 'Before the Ruach fully established form, the forces were unified. Electromagnetism and the weak force were one. The boundary between light and boundary-setter had not yet been drawn.',
    scripture: '"The earth was formless and empty — תֹהוּ וָבֹהוּ." — Genesis 1:2. The distinctions of creation have not yet fully appeared.',
    particles: { electron: true, quark: true, w: 'massless', z: 'massless', photon: true, higgs: 'field only' },
    forcesMerged: ['EM + Weak → Electroweak'],
    note: 'W & Z are massless here — Higgs field has not yet broken symmetry',
  },
  {
    t: 0.85,
    label: 'Grand Unified era (theoretical)',
    hebrewLabel: 'אֶחָד — One',
    temp: '~10²⁸K',
    energy: '~10¹⁵ GeV',
    description: 'Theoretical: electroweak and strong forces may unify into a single force. All matter particles may be interchangeable. This era is beyond current experimental reach.',
    ruach: 'In theory — a moment when the strong binding, the boundaries, and the light were all one force. All distinctions dissolved. Only the Ruach, undivided.',
    scripture: '"Hear, O Israel: The LORD our God, the LORD is one." — Deuteronomy 6:4 — the Shema resonates here.',
    particles: { electron: 'unified', quark: 'unified', w: 'massless', z: 'massless', photon: true, higgs: 'none' },
    forcesMerged: ['EM + Weak + Strong → GUT force'],
    note: 'Beyond current experimental verification — theoretical',
  },
  {
    t: 1.0,
    label: 'Tohu va-Bohu — תֹהוּ וָבֹהוּ',
    hebrewLabel: 'תֹהוּ וָבֹהוּ',
    temp: '~10³²K (Planck)',
    energy: '~10¹⁹ GeV',
    description: 'The Planck era. Space, time, and energy are indistinguishable. No particles exist as we know them. The Higgs field has no meaning. Formless. Void.',
    ruach: '"In the beginning God created the heavens and the earth. Now the earth was formless and empty — תֹהוּ וָבֹהוּ — darkness was over the surface of the deep, and the Spirit of God was hovering over the waters." — Genesis 1:1-2',
    scripture: 'Genesis 1:1-2 — the moment before the first utterance',
    particles: { electron: false, quark: false, w: false, z: false, photon: false, higgs: false },
    forcesMerged: ['All forces unified — or undefined'],
    note: 'Beyond all current physics — the boundary of knowledge',
  },
];

function ParticleStatus({ label, active, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
      <div style={{
        width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
        background: active === true ? color : active === 'massless' ? color : active === 'unified' ? '#888' : 'transparent',
        border: `1px solid ${active ? color : 'rgba(255,255,255,0.2)'}`,
        opacity: active ? 1 : 0.3,
        boxShadow: active === true ? `0 0 4px ${color}` : 'none',
      }} />
      <span style={{ fontSize: '0.7rem', fontFamily: 'Space Mono', color: active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)' }}>
        {label}
        {active === 'massless' && <span style={{ color: '#ff9090', marginLeft: '4px' }}>massless</span>}
        {active === 'unified' && <span style={{ color: '#888', marginLeft: '4px' }}>unified</span>}
        {active === 'field only' && <span style={{ color: '#f0c04060', marginLeft: '4px' }}>field only</span>}
      </span>
    </div>
  );
}

export default function TimelineSlider({ mode }) {
  const [eraIndex, setEraIndex] = useState(0);
  const isRuach = mode === 'ruach';
  const era = ERAS[eraIndex];
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1.5rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'Drag back to Tohu va-Bohu — תֹהוּ וָבֹהוּ' : 'Drag back to the Big Bang — watch symmetry break'}
      </p>

      {/* Slider */}
      <div style={{ marginBottom: '1.5rem' }}>
        <input
          type="range" min={0} max={ERAS.length - 1} step={1}
          value={eraIndex}
          onChange={e => setEraIndex(Number(e.target.value))}
          style={{ width: '100%', accentColor }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.25)', marginTop: '4px' }}>
          <span>Now</span>
          <span style={{ color: isRuach ? '#ffb06070' : '#f0c04070' }}>{isRuach ? 'תֹהוּ וָבֹהוּ' : 'Planck era'}</span>
        </div>
      </div>

      {/* Era display */}
      <div style={{
        borderRadius: '12px', overflow: 'hidden',
        border: `1px solid ${eraIndex === ERAS.length - 1 ? accentColor + '50' : 'rgba(255,255,255,0.1)'}`,
        transition: 'border-color 0.3s',
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          background: eraIndex === ERAS.length - 1 ? `${accentColor}12` : 'rgba(255,255,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          transition: 'background 0.3s',
        }}>
          <div style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', color: accentColor, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.3rem' }}>
            {isRuach ? era.hebrewLabel : `Era ${eraIndex + 1} of ${ERAS.length}`}
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, fontFamily: 'Syne', color: '#fff' }}>
            {isRuach && eraIndex === ERAS.length - 1 ? 'תֹהוּ וָבֹהוּ' : era.label}
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
            <div style={{ fontSize: '0.7rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.4)' }}>
              T: {era.temp}
            </div>
            <div style={{ fontSize: '0.7rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.4)' }}>
              E: {era.energy}
            </div>
          </div>
        </div>

        <div style={{ padding: '1rem 1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {/* Description */}
          <div style={{ gridColumn: '1 / -1' }}>
            <p style={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, margin: 0 }}>
              {isRuach ? era.ruach : era.description}
            </p>
            {isRuach && era.scripture && (
              <p style={{ fontSize: '0.75rem', color: `${accentColor}80`, fontStyle: 'italic', lineHeight: 1.6, margin: '0.6rem 0 0' }}>
                {era.scripture}
              </p>
            )}
          </div>

          {/* Particle status */}
          <div>
            <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.25)', marginBottom: '0.6rem' }}>
              {isRuach ? 'Formed things' : 'Particle status'}
            </div>
            <ParticleStatus label="Electron" active={era.particles.electron} color="#50e0a0" />
            <ParticleStatus label="Quarks" active={era.particles.quark} color="#4a9eff" />
            <ParticleStatus label="Photon (γ)" active={era.particles.photon} color="#ffffff" />
            <ParticleStatus label="W boson" active={era.particles.w} color="#ff9040" />
            <ParticleStatus label="Z boson" active={era.particles.z} color="#ff9040" />
            <ParticleStatus label={isRuach ? 'Ruach (Higgs)' : 'Higgs field'} active={era.particles.higgs} color={accentColor} />
          </div>

          {/* Forces */}
          <div>
            <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.25)', marginBottom: '0.6rem' }}>
              {isRuach ? 'Pillars of creation' : 'Force unification'}
            </div>
            {era.forcesMerged.length === 0 ? (
              <div>
                {['Gravity', 'Strong', 'Weak', 'EM'].map(f => (
                  <div key={f} style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', marginBottom: '3px', fontFamily: 'Space Mono' }}>
                    · {f} — distinct
                  </div>
                ))}
              </div>
            ) : (
              era.forcesMerged.map((f, i) => (
                <div key={i} style={{
                  fontSize: '0.72rem', padding: '4px 8px', borderRadius: '4px', marginBottom: '4px',
                  background: `${accentColor}15`, color: accentColor, fontFamily: 'Space Mono',
                }}>
                  {f}
                </div>
              ))
            )}
            {era.note && (
              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.5rem', fontStyle: 'italic', lineHeight: 1.5 }}>
                {era.note}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Era dots navigation */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '1rem' }}>
        {ERAS.map((e, i) => (
          <button key={i} onClick={() => setEraIndex(i)} style={{
            width: i === eraIndex ? '20px' : '8px', height: '8px',
            borderRadius: '4px', border: 'none', cursor: 'pointer',
            background: i === eraIndex ? accentColor : 'rgba(255,255,255,0.2)',
            transition: 'all 0.2s', padding: 0,
          }} />
        ))}
      </div>
    </div>
  );
}
